<template>
  <div
    class="org-page agent-config-tab-pane"
    :class="{ 'org-page--embedded': embedded }"
  >
    <SnackbarComp :SnackbarComponent="SnackbarComponent" />

    <UpdateKnowledgeBaseDialog
      v-model="showEditDialog"
      :organization="organizationForDialog"
      @saved="onKnowledgeBaseSaved"
      @error="onKnowledgeBaseError"
    />

    <DeleteKnowledgeBaseFileDialog
      v-model="showDeleteFileDialog"
      :file-label="filePendingDelete?.label || ''"
      :deleting="deletingKbFile"
      @confirm="confirmDeleteEmbeddedFile"
    />

    <div
      v-if="!pageLoading && hasKnowledgeBaseContent"
      class="org-kb-search"
    >
      <v-text-field
        v-model="kbSearchQuery"
        placeholder="Search for a keyword or phrase inside your knowledge base..."
        prepend-inner-icon="mdi-magnify"
        variant="solo"
        density="comfortable"
        hide-details="auto"
        clearable
        flat
        class="org-kb-search__input"
        :loading="kbSearching"
        @keydown.enter="runKnowledgeBaseSearch"
        @click:clear="clearKnowledgeBaseSearch"
      />
      <p v-if="kbSearchError" class="org-kb-search__error">{{ kbSearchError }}</p>

      <div v-if="kbSearchResults !== null" class="org-kb-search__results">
        <p v-if="!kbSearchResults.length" class="org-kb-search__empty">
          No matches found for "{{ kbSearchedFor }}".
        </p>
        <template v-else>
          <p class="org-kb-search__count">
            {{ kbSearchResults.length }} match{{ kbSearchResults.length === 1 ? '' : 'es' }} for "{{ kbSearchedFor }}"
          </p>
          <div
            v-for="(result, idx) in kbSearchResults"
            :key="idx"
            class="org-kb-search__result"
          >
            <div class="org-kb-search__result-head">
              <v-icon icon="mdi-file-document-outline" size="16" />
              <span class="org-kb-search__result-file">{{ result.filename || 'Unknown source' }}</span>
            </div>
            <p class="org-kb-search__result-text">{{ result.text }}</p>
          </div>
        </template>
      </div>
    </div>

    <div
      v-if="pageLoading"
      class="org-page__loading d-flex align-center justify-center"
    >
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <template v-else-if="embedded">
      <div class="kb-premium">
        <div class="kb-premium__shell">
          <div class="kb-premium__glow" aria-hidden="true" />

          <header class="kb-premium__header">
            <div class="kb-premium__brand">
              <span class="kb-premium__icon" aria-hidden="true">
                <v-icon icon="mdi-brain" size="20" />
              </span>
              <div class="min-w-0">
                <span class="kb-premium__eyebrow">Knowledge library</span>
                <h3 class="kb-premium__title">Training data &amp; documents</h3>
              </div>
            </div>
          </header>

          <div
            v-if="!hasEmbeddedKnowledgeContent"
            class="kb-premium__empty-state kb-premium__empty-state--full"
          >
            <span class="kb-premium__empty-icon" aria-hidden="true">
              <v-icon icon="mdi-book-open-page-variant-outline" size="26" />
            </span>
            <p class="kb-premium__empty-title">No knowledge base yet</p>
            <p class="kb-premium__empty-text">
              Use Knowledgebase to add manual text or upload training files.
            </p>
          </div>

          <div v-else class="kb-premium__split">
            <aside class="kb-premium__sidebar">
              <div class="kb-premium__sidebar-head">
                <span class="kb-premium__sidebar-title">Sources</span>
                <span class="kb-premium__sidebar-count">
                  {{ embeddedSourceChips.length }}
                </span>
              </div>

              <ul
                class="kb-premium__source-list"
                role="tablist"
                aria-label="Knowledge sources"
              >
                <li
                  v-for="chip in embeddedSourceChips"
                  :key="chip.id"
                  class="kb-premium__source-row"
                >
                  <button
                    type="button"
                    role="tab"
                    class="kb-premium__source-item"
                    :class="{
                      'kb-premium__source-item--active':
                        embeddedSelectedSourceId === chip.id,
                    }"
                    :aria-selected="embeddedSelectedSourceId === chip.id"
                    @click="selectEmbeddedSource(chip)"
                  >
                    <span
                      class="kb-premium__source-item-icon"
                      :class="{
                        'kb-premium__source-item-icon--manual':
                          chip.type === 'manual',
                      }"
                    >
                      <v-icon :icon="chip.icon" size="16" />
                    </span>
                    <span class="kb-premium__source-item-copy min-w-0">
                      <span
                        class="kb-premium__source-item-label"
                        :title="chip.label"
                      >
                        {{ chip.label }}
                      </span>
                      <span class="kb-premium__source-item-type">
                        {{
                          chip.type === 'manual' ? 'Manual entry' : 'Text file'
                        }}
                      </span>
                    </span>
                    <v-progress-circular
                      v-if="
                        embeddedContentLoading &&
                        embeddedSelectedSourceId === chip.id
                      "
                      indeterminate
                      size="14"
                      width="2"
                      class="kb-premium__source-item-spinner"
                    />
                  </button>
                  <button
                    v-if="chip.type === 'file'"
                    type="button"
                    class="kb-premium__source-delete"
                    :disabled="deletingKbFile"
                    aria-label="Delete file"
                    @click.stop="openDeleteFileDialog(chip)"
                  >
                    <v-icon icon="mdi-delete-outline" size="16" />
                  </button>
                </li>
              </ul>
            </aside>

            <main class="kb-premium__main">
              <div
                v-if="!embeddedContentLoading"
                class="kb-premium__meta"
              >
                <span class="kb-premium__meta-pill kb-premium__meta-pill--accent">
                  <v-icon icon="mdi-file-document-outline" size="12" />
                  {{ embeddedSourceMetaLabel }}
                </span>
                <span class="kb-premium__meta-sep" aria-hidden="true" />
                <span class="kb-premium__meta-pill">
                  {{ embeddedCharCount }} characters
                </span>
                <span class="kb-premium__meta-sep" aria-hidden="true" />
                <span>{{ lastUpdatedLabel }} {{ lastUpdatedDisplay }}</span>
              </div>

              <div class="kb-premium__viewport">
                <div class="kb-premium__viewport-accent" aria-hidden="true" />

                <div
                  v-if="embeddedContentLoading"
                  class="kb-premium__loading"
                >
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="28"
                    width="3"
                  />
                </div>
                <div
                  v-else-if="embeddedDisplayText"
                  class="kb-premium__viewport-scroll"
                >
                  <article class="kb-premium__article">
                    {{ embeddedDisplayText }}
                  </article>
                </div>
                <p v-else class="kb-premium__empty-inline mb-0">
                  No content available for this source.
                </p>
              </div>
            </main>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div
        class="org-page__header d-flex align-center justify-space-between flex-wrap ga-3"
      >
        <div class="min-w-0">
          <h2 class="org-page__title text-truncate">Knowledge base</h2>
          <p class="org-page__subtitle mb-0">
            <v-icon icon="mdi-clock-outline" size="14" class="mr-1" />
            {{ lastUpdatedLabel }} {{ lastUpdatedDisplay }}
          </p>
        </div>
        <div class="d-flex align-center ga-2 flex-shrink-0">
          <YandleAgentFab
            variant="inline"
            trigger-label="Test agent"
            :org-name="displayOrganization.org_name"
          />
          <v-btn-toggle
            v-if="showViewToggle"
            v-model="viewMode"
            mandatory
            divided
            color="primary"
            density="compact"
            class="kb-mode-toggle kb-mode-toggle--header"
          >
            <v-btn value="manual" size="small" class="text-none">Manual</v-btn>
            <v-btn value="upload" size="small" class="text-none">Upload</v-btn>
          </v-btn-toggle>
          <v-btn
            color="primary"
            size="small"
            prepend-icon="mdi-pencil-outline"
            class="text-none btn-primary-cta"
            rounded="lg"
            :disabled="!isSessionReady"
            @click="showEditDialog = true"
          >
            Edit
          </v-btn>
        </div>
      </div>

      <div class="org-summary">
        <template v-if="hasKnowledgeBaseContent">
          <div v-if="showManualView" class="org-detail-kb org-kb-view-panel">
            <p v-if="knowledgeBaseDisplayText" class="org-detail-kb__text">
              {{ knowledgeBaseDisplayText }}
            </p>
          </div>

          <div
            v-else-if="showUploadView"
            class="org-kb-files org-kb-view-panel"
          >
            <div class="org-kb-files__layout">
              <aside class="org-kb-files__list rounded-lg">
                <p class="org-kb-files__heading px-3 pt-3 mb-1">
                  Files
                  <span class="org-kb-files__count"
                    >({{ fileEntries.length }})</span
                  >
                </p>
                <ul class="org-kb-files__items px-2 pb-2 mb-0">
                  <li
                    v-for="entry in fileEntries"
                    :key="entry.id"
                    class="org-kb-files__row"
                  >
                    <button
                      type="button"
                      class="org-kb-files__item"
                      :class="{
                        'org-kb-files__item--active':
                          selectedFileId === entry.id,
                      }"
                      @click="selectFileEntry(entry)"
                    >
                      <span
                        class="org-kb-files__icon-wrap"
                        :style="fileIconWrapStyle(entry.kind)"
                      >
                        <v-icon
                          :icon="fileIcon(entry.kind)"
                          size="16"
                          :color="fileIconColor(entry.kind)"
                        />
                      </span>
                      <span class="org-kb-files__label text-truncate">{{
                        entry.label
                      }}</span>
                      <v-icon
                        v-if="
                          selectedFileId === entry.id &&
                          selectedPreview?.loading
                        "
                        icon="mdi-loading"
                        size="14"
                        class="org-kb-files__spinner"
                      />
                    </button>
                  </li>
                </ul>
              </aside>

              <section class="org-kb-files__preview rounded-lg">
                <div
                  v-if="selectedPreview"
                  class="org-kb-preview org-kb-preview--panel"
                >
                  <div
                    class="org-kb-preview__head d-flex align-center justify-space-between px-3 py-2"
                  >
                    <div class="d-flex align-center ga-2 min-w-0">
                      <span
                        class="org-kb-files__icon-wrap org-kb-files__icon-wrap--sm flex-shrink-0"
                        :style="fileIconWrapStyle(selectedPreview.kind)"
                      >
                        <v-icon
                          :icon="fileIcon(selectedPreview.kind)"
                          size="14"
                          :color="fileIconColor(selectedPreview.kind)"
                        />
                      </span>
                      <span class="org-kb-preview__title text-truncate">
                        {{ selectedPreview.label }}
                      </span>
                    </div>
                    <v-btn
                      v-if="
                        selectedPreview.kind === 'pdf' &&
                        selectedPreview.url &&
                        !selectedPreview.loading
                      "
                      size="x-small"
                      variant="text"
                      class="text-none"
                      @click="openPreviewExternally"
                    >
                      Open
                    </v-btn>
                  </div>

                  <div class="org-kb-preview__body">
                    <div
                      v-if="selectedPreview.loading"
                      class="org-kb-preview--loading d-flex align-center justify-center"
                    >
                      <v-progress-circular
                        indeterminate
                        color="primary"
                        size="28"
                        width="3"
                      />
                    </div>

                    <embed
                      v-else-if="
                        selectedPreview.kind === 'pdf' && selectedPreview.url
                      "
                      :src="selectedPreview.url"
                      type="application/pdf"
                      class="org-kb-preview__pdf"
                    />

                    <pre
                      v-else-if="
                        selectedPreview.kind === 'text' &&
                        selectedPreview.text != null
                      "
                      class="org-kb-preview__text"
                      >{{ selectedPreview.text }}</pre
                    >

                    <p v-else class="org-kb-preview__empty-msg px-3 py-3 mb-0">
                      Preview unavailable
                    </p>
                  </div>
                </div>

                <div
                  v-else
                  class="org-kb-preview org-kb-preview--empty d-flex align-center justify-center"
                >
                  <p class="org-kb-preview__empty-msg pa-3 mb-0 text-center">
                    Select a file to preview
                  </p>
                </div>
              </section>
            </div>
          </div>
        </template>

        <div
          v-else
          class="org-kb-empty d-flex flex-column align-center justify-center text-center"
        >
          <v-icon
            icon="mdi-book-open-page-variant-outline"
            size="48"
            color="primary"
            class="mb-3"
          />
          <p class="org-kb-empty__title mb-1">No knowledge base yet</p>
          <p class="org-kb-empty__text mb-4">
            Add manual text or upload files to train your voice agent.
          </p>
          <v-btn
            color="primary"
            size="small"
            prepend-icon="mdi-plus"
            class="text-none"
            rounded="lg"
            :disabled="!isSessionReady"
            @click="showEditDialog = true"
          >
            Add knowledge base
          </v-btn>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import SnackbarComp from "@/components/Extras/SnackbarComp.vue";
import UpdateKnowledgeBaseDialog from "@/components/Organizations/UpdateKnowledgeBaseDialog.vue";
import DeleteKnowledgeBaseFileDialog from "@/components/Organizations/DeleteKnowledgeBaseFileDialog.vue";
import YandleAgentFab from "@/components/Extras/YandleAgentFab.vue";
import organizationDisplayMixin from "@/mixins/Organization/organizationDisplayMixin";
import knowledgeBasePreviewMixin from "@/mixins/Organization/knowledgeBasePreviewMixin.js";
import {
  isOrganizationLogoS3Key,
  isKnowledgeBaseS3Key,
  filterKnowledgeBasePreviewKeys,
} from "@/mixins/Organization/OrganizationStorage.js";
import {
  getKnowledgeBaseFileVisual,
  knowledgeBaseFileIconStyle,
} from "@/utils/knowledgeBaseFiles.js";
import { listKnowledgeBaseS3Keys, removeKnowledgeBaseS3File } from "@/utils/knowledgeBaseS3.js";
import { searchKnowledgeBase } from "@/api/knowledgeBaseSearchApi.js";
import { isApiSuccess } from "@/mixins/Extras/ApiResponse";
export default {
  name: "KnowledgeBaseTab",
  components: {
    SnackbarComp,
    UpdateKnowledgeBaseDialog,
    DeleteKnowledgeBaseFileDialog,
    YandleAgentFab,
  },
  mixins: [organizationDisplayMixin, knowledgeBasePreviewMixin],
  props: {
    embedded: { type: Boolean, default: false },
    active: { type: Boolean, default: false },
  },
  emits: ['saved', 'edit-dialog-change'],
  data() {
    return {
      SnackbarComponent: {},
      showEditDialog: false,
      viewMode: "manual",
      embeddedSelectedSourceId: null,
      embeddedFileText: "",
      embeddedContentLoading: false,
      embeddedTextCache: {},
      embeddedContentRequestId: 0,
      listedKbS3Keys: [],
      listedKbLoading: false,
      showDeleteFileDialog: false,
      filePendingDelete: null,
      deletingKbFile: false,
      kbSearchQuery: "",
      kbSearchedFor: "",
      kbSearchResults: null,
      kbSearching: false,
      kbSearchError: "",
    };
  },
  computed: {
    hasKnowledgeBaseContent() {
      return this.hasManualText || this.hasUploadedFiles;
    },
    showViewToggle() {
      return this.hasManualText && this.hasUploadedFiles;
    },
    showManualView() {
      return (
        this.hasManualText &&
        (this.viewMode === "manual" || !this.hasUploadedFiles)
      );
    },
    showUploadView() {
      return (
        this.hasUploadedFiles &&
        (this.viewMode === "upload" || !this.hasManualText)
      );
    },
    knowledgeBaseS3Keys() {
      return filterKnowledgeBasePreviewKeys(this.listedKbS3Keys);
    },
    hasUploadedFiles() {
      return this.knowledgeBaseS3Keys.length > 0;
    },
    hasManualText() {
      const text = this.displayOrganization.org_knowledeg_base;
      if (
        !text ||
        isOrganizationLogoS3Key(text) ||
        isKnowledgeBaseS3Key(text)
      ) {
        return false;
      }
      return String(text).trim().length > 0;
    },
    fileEntries() {
      return this.dedupeFileEntriesByLabel(
        this.buildExistingFileEntries(
          this.knowledgeBaseS3Keys,
          this.displayOrganization.org_id,
        ),
      );
    },
    txtFileEntries() {
      return this.fileEntries.filter((entry) => entry.kind === "text");
    },
    embeddedSourceChips() {
      const chips = [];

      if (this.hasManualText) {
        chips.push({
          id: "manual",
          type: "manual",
          label: "Manual",
          icon: "mdi-text-box-outline",
        });
      }

      // Every document, not only the text ones. A PDF cannot be shown as text
      // in this panel, but it IS part of the knowledge base and hiding it made
      // uploads look like they had silently failed.
      for (const entry of this.fileEntries) {
        chips.push({
          id: entry.id,
          type: "file",
          label: entry.label,
          icon:
            entry.kind === "pdf"
              ? "mdi-file-pdf-box"
              : "mdi-file-document-outline",
          entry,
        });
      }

      return chips;
    },
    embeddedDisplayText() {
      const chip = this.embeddedSourceChips.find(
        (item) => item.id === this.embeddedSelectedSourceId,
      );
      if (!chip) return "";
      if (chip.type === "manual") return this.knowledgeBaseDisplayText;
      if (chip.entry?.kind === "pdf") {
        return (
          "This is a PDF document. Its text cannot be previewed here, but it " +
          "has been indexed and the AI can answer from it on calls.\n\n" +
          "Use Edit Knowledgebase to replace or remove it."
        );
      }
      return this.embeddedFileText;
    },
    hasEmbeddedKnowledgeContent() {
      return this.hasManualText || this.fileEntries.length > 0;
    },
    embeddedActiveChip() {
      return (
        this.embeddedSourceChips.find(
          (chip) => chip.id === this.embeddedSelectedSourceId,
        ) || null
      );
    },
    embeddedSourceMetaLabel() {
      const chip = this.embeddedActiveChip;
      if (!chip) return "—";
      return chip.type === "manual" ? "Manual entry" : chip.label;
    },
    embeddedCharCount() {
      const text = this.embeddedDisplayText || "";
      return text.length.toLocaleString();
    },
    knowledgeBaseDisplayText() {
      const text = this.displayOrganization.org_knowledeg_base;
      if (
        !text ||
        isOrganizationLogoS3Key(text) ||
        isKnowledgeBaseS3Key(text)
      ) {
        return "";
      }
      return String(text).trim();
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(isActive) {
        if (isActive) {
          void this.refreshListedKnowledgeBaseFiles();
        }
        if (isActive && this.embedded) {
          this.initEmbeddedSelection();
        }
      },
    },
    "displayOrganization.org_id": {
      immediate: true,
      handler() {
        void this.refreshListedKnowledgeBaseFiles();
        this.viewMode = this.hasUploadedFiles ? "upload" : "manual";
      },
    },
    embeddedSourceChips: {
      immediate: true,
      handler() {
        if (this.embedded) {
          this.initEmbeddedSelection();
        }
      },
    },
    fileEntries: {
      handler(entries) {
        if (
          this.selectedFileId &&
          !entries.some((e) => e.id === this.selectedFileId)
        ) {
          this.closePreview();
        }
        if (this.showUploadView) {
          this.autoSelectFirstFileEntry(entries);
        }
      },
      immediate: true,
    },
    showEditDialog(open) {
      this.$emit("edit-dialog-change", open);
    },
    showUploadView(active) {
      if (!active) {
        this.closePreview();
        return;
      }
      this.$nextTick(() => this.autoSelectFirstFileEntry(this.fileEntries));
    },
    hasKnowledgeBaseContent: {
      immediate: true,
      handler() {
        this.syncViewMode();
      },
    },
    "displayOrganization.kb_s3_prefix"() {
      void this.refreshListedKnowledgeBaseFiles();
    },
    viewMode(mode) {
      if (mode === "upload") {
        this.$nextTick(() => this.autoSelectFirstFileEntry(this.fileEntries));
      } else {
        this.closePreview();
      }
    },
  },
  methods: {
    openEditDialog() {
      this.showEditDialog = true;
    },
    async runKnowledgeBaseSearch() {
      const query = String(this.kbSearchQuery || "").trim();
      if (!query) return;

      this.kbSearching = true;
      this.kbSearchError = "";
      const internalOrgId = this.displayOrganization.internal_org_id;
      if (!internalOrgId) {
        this.kbSearching = false;
        this.kbSearchError = "This organization has no internal_org_id on file -- search is unavailable.";
        return;
      }

      const response = await searchKnowledgeBase({ query, internalOrgId });
      this.kbSearching = false;
      this.kbSearchedFor = query;

      if (isApiSuccess(response)) {
        this.kbSearchResults = response.results || [];
      } else {
        this.kbSearchResults = null;
        this.kbSearchError = response.message || "Search failed. Please try again.";
      }
    },
    clearKnowledgeBaseSearch() {
      this.kbSearchQuery = "";
      this.kbSearchedFor = "";
      this.kbSearchResults = null;
      this.kbSearchError = "";
    },
    dedupeFileEntriesByLabel(entries) {
      const scoreEntry = (entry) => {
        const key = String(entry.key || "");
        let score = key.length;
        if (/-.+_overview\.(txt|pdf)$/i.test(key)) score += 10000;
        return score;
      };

      const sorted = [...entries].sort(
        (a, b) => scoreEntry(b) - scoreEntry(a),
      );
      const byLabel = new Map();

      for (const entry of sorted) {
        if (!byLabel.has(entry.label)) {
          byLabel.set(entry.label, entry);
        }
      }

      return Array.from(byLabel.values()).sort((a, b) =>
        String(a.label || "").localeCompare(String(b.label || "")),
      );
    },
    async refreshAfterKnowledgeBaseSave() {
      const previousChipId = this.embeddedSelectedSourceId;
      this.embeddedTextCache = {};
      this.embeddedContentRequestId += 1;
      await this.refreshListedKnowledgeBaseFiles();

      if (!this.embedded) return;

      await this.$nextTick();
      const chips = this.embeddedSourceChips;
      if (!chips.length) {
        this.embeddedSelectedSourceId = null;
        this.embeddedFileText = "";
        this.embeddedContentLoading = false;
        return;
      }

      const fileChips = chips.filter((item) => item.type === "file");
      const chip =
        chips.find((item) => item.id === previousChipId) ||
        (fileChips.length
          ? fileChips.reduce((best, item) => {
              const score = String(item.entry?.key || "").length;
              const bestScore = String(best.entry?.key || "").length;
              return score > bestScore ? item : best;
            })
          : null) ||
        chips.find((item) => item.type === "manual") ||
        chips[0];

      this.embeddedSelectedSourceId = chip.id;
      if (chip.type === "file" && chip.entry) {
        await this.loadEmbeddedFileContent(chip.entry, { force: true });
        return;
      }

      this.embeddedFileText = "";
      this.embeddedContentLoading = false;
    },
    async refreshListedKnowledgeBaseFiles() {
      const org = this.displayOrganization;
      if (!org?.org_id && !org?.kb_s3_prefix) {
        this.listedKbS3Keys = [];
        return;
      }

      this.listedKbLoading = true;
      try {
        this.listedKbS3Keys = await listKnowledgeBaseS3Keys(org);
      } catch (error) {
        console.log("Err", error);
        this.listedKbS3Keys = [];
      } finally {
        this.listedKbLoading = false;
      }
    },
    initEmbeddedSelection() {
      const chips = this.embeddedSourceChips;
      if (!chips.length) {
        this.embeddedSelectedSourceId = null;
        this.embeddedFileText = "";
        this.embeddedContentLoading = false;
        return;
      }

      const current = chips.find(
        (chip) => chip.id === this.embeddedSelectedSourceId,
      );
      if (current) {
        if (current.type === "file" && current.entry?.kind !== "pdf") {
          void this.loadEmbeddedFileContent(current.entry);
        }
        return;
      }

      const preferred = chips.find((chip) => chip.type === "manual") || chips[0];
      void this.selectEmbeddedSource(preferred);
    },
    async selectEmbeddedSource(chip) {
      if (!chip) return;

      this.embeddedSelectedSourceId = chip.id;

      if (chip.type === "manual" || chip.entry?.kind === "pdf") {
        this.embeddedContentLoading = false;
        this.embeddedFileText = "";
        return;
      }

      await this.loadEmbeddedFileContent(chip.entry);
    },
    async loadEmbeddedFileContent(entry, options = {}) {
      const { force = false } = options;
      const cacheKey = entry?.key || entry?.id;
      if (!cacheKey) return;

      if (!force && this.embeddedTextCache[cacheKey] != null) {
        this.embeddedFileText = this.embeddedTextCache[cacheKey];
        this.embeddedContentLoading = false;
        return;
      }

      if (force) {
        delete this.embeddedTextCache[cacheKey];
      }

      const requestId = ++this.embeddedContentRequestId;
      this.embeddedContentLoading = true;
      this.embeddedFileText = "";

      try {
        const text = (await this.getS3TextContent(entry.key)) ?? "";
        if (requestId !== this.embeddedContentRequestId) return;
        this.embeddedTextCache[cacheKey] = text;
        this.embeddedFileText = text;
      } catch (error) {
        console.log("Err", error);
        if (requestId !== this.embeddedContentRequestId) return;
        this.embeddedFileText = "";
      } finally {
        if (requestId === this.embeddedContentRequestId) {
          this.embeddedContentLoading = false;
        }
      }
    },
    fileIcon(kind) {
      return getKnowledgeBaseFileVisual(kind).icon;
    },
    fileIconColor(kind) {
      return getKnowledgeBaseFileVisual(kind).color;
    },
    fileIconWrapStyle(kind) {
      return knowledgeBaseFileIconStyle(kind);
    },
    syncViewMode() {
      if (this.hasUploadedFiles && !this.hasManualText) {
        this.viewMode = "upload";
      } else if (this.hasManualText && !this.hasUploadedFiles) {
        this.viewMode = "manual";
      } else if (this.viewMode === "upload" && !this.hasUploadedFiles) {
        this.viewMode = "manual";
      } else if (this.viewMode === "manual" && !this.hasManualText) {
        this.viewMode = "upload";
      }
    },
    onKnowledgeBaseError(payload) {
      this.onError(payload, "Failed to save knowledge base.");
    },
    openDeleteFileDialog(chip) {
      if (!chip?.entry?.key) return;
      this.filePendingDelete = chip;
      this.showDeleteFileDialog = true;
    },
    async confirmDeleteEmbeddedFile() {
      const s3Key = this.filePendingDelete?.entry?.key;
      if (!s3Key) return;

      this.deletingKbFile = true;
      try {
        await removeKnowledgeBaseS3File(s3Key);

        if (this.embeddedSelectedSourceId === this.filePendingDelete?.id) {
          this.embeddedSelectedSourceId = null;
          this.embeddedFileText = "";
        }

        delete this.embeddedTextCache[s3Key];
        await this.refreshListedKnowledgeBaseFiles();
        this.showDeleteFileDialog = false;
        this.filePendingDelete = null;
        this.$nextTick(() => this.initEmbeddedSelection());
        this.SnackbarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "success",
          SnackbarText: "Knowledge file deleted successfully.",
        };
      } catch (error) {
        console.log("Err", error);
        this.onError(error, "Failed to delete knowledge file.");
      } finally {
        this.deletingKbFile = false;
      }
    },
    async onKnowledgeBaseSaved(response) {
      this.showEditDialog = false;
      this.closePreview();
      await this.refreshAfterKnowledgeBaseSave();

      if (this.embedded) {
        this.$emit("saved", response);
        return;
      }

      await this.onSaved(response, "Knowledge base saved successfully.");
      this.syncViewMode();
      this.$nextTick(() => {
        if (this.showUploadView) {
          this.autoSelectFirstFileEntry(this.fileEntries);
        }
      });
    },
  },
};
</script>

<style scoped>
.org-kb-search {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: var(--y-space-2);
  margin-bottom: var(--y-space-2);
}

.org-kb-search__input {
  border-radius: var(--y-radius-md, 10px);
  background: var(--y-surface);
  border: 1px solid var(--y-border);
}

.org-kb-search__error {
  margin: 0;
  font-size: var(--y-font-size-sm, 0.8125rem);
  color: #ef4444;
}

.org-kb-search__results {
  display: flex;
  flex-direction: column;
  gap: var(--y-space-2);
  max-height: 320px;
  overflow-y: auto;
  padding-right: 2px;
}

.org-kb-search__empty,
.org-kb-search__count {
  margin: 0;
  font-size: var(--y-font-size-sm, 0.8125rem);
  color: var(--y-text-muted);
}

.org-kb-search__result {
  padding: var(--y-space-2) var(--y-space-3);
  border: 1px solid var(--y-border);
  border-radius: var(--y-radius-md, 10px);
  background: var(--y-surface);
}

.org-kb-search__result-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  font-size: var(--y-font-size-xs, 0.75rem);
  font-weight: 600;
  color: var(--y-text-muted);
}

.org-kb-search__result-text {
  margin: 0;
  font-size: var(--y-font-size-sm, 0.8125rem);
  color: var(--y-text);
  line-height: 1.5;
  white-space: pre-wrap;
}

.org-page--embedded {
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0;
  overflow: hidden;
}

.agent-config-tab-pane {
  height: 100%;
  min-height: 0;
}

.org-page {
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--y-space-3);
  overflow: hidden;
  padding: var(--y-space-2);
  box-sizing: border-box;
}

.org-page__loading {
  flex: 1;
  min-height: 120px;
}

.org-page__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--y-text);
  letter-spacing: -0.02em;
}

.org-page__subtitle {
  margin-top: 4px;
  font-size: 0.6875rem;
  color: var(--y-text-muted);
  display: inline-flex;
  align-items: center;
}

.org-summary {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: var(--y-space-4);
  border-radius: var(--y-radius-md);
  background: var(--y-panel-bg);
  border: 1px solid var(--y-panel-border);
}

.kb-mode-toggle--header {
  height: 32px;
  border: 1px solid var(--y-glass-border);
  border-radius: 10px;
  overflow: hidden;
  background: var(--y-glass-surface);
}

.kb-mode-toggle--header :deep(.v-btn) {
  min-width: 72px;
  height: 32px !important;
  font-size: 0.75rem;
  letter-spacing: 0;
  color: var(--y-text-secondary) !important;
  background: transparent !important;
}

.kb-mode-toggle--header :deep(.v-btn--active),
.kb-mode-toggle--header :deep(.v-btn--selected) {
  color: #ffffff !important;
  background: rgba(99, 102, 241, 0.4) !important;
}

.org-kb-view-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.org-kb-files {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.org-kb-files__layout {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(180px, 240px) 1fr;
  gap: var(--y-space-3);
}

.org-kb-files__list {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  border: 1px solid var(--y-glass-border);
  background: var(--y-glass-surface);
}

.org-kb-files__heading {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--y-text-secondary);
  margin: 0;
  flex-shrink: 0;
}

.org-kb-files__count {
  font-weight: 500;
  color: var(--y-text-muted);
}

.org-kb-files__items {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.org-kb-files__row {
  display: flex;
}

.org-kb-files__item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--y-text);
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.org-kb-files__item:hover {
  background: rgba(99, 102, 241, 0.12);
}

.org-kb-files__item--active {
  border-color: #6366f1;
  background: rgba(99, 102, 241, 0.18);
}

.org-kb-files__icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  flex-shrink: 0;
}

.org-kb-files__icon-wrap--sm {
  width: 24px;
  height: 24px;
}

.org-kb-files__label {
  flex: 1;
  min-width: 0;
  font-size: 0.8125rem;
}

.org-kb-files__spinner {
  flex-shrink: 0;
  animation: org-kb-spin 0.8s linear infinite;
}

@keyframes org-kb-spin {
  to {
    transform: rotate(360deg);
  }
}

.org-kb-files__preview {
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--y-glass-border);
  background: var(--y-glass-surface);
}

.org-kb-preview {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.org-kb-preview--empty {
  border-style: dashed;
  background: var(--y-panel-bg);
}

.org-kb-preview__head {
  flex-shrink: 0;
  border-bottom: 1px solid var(--y-glass-border);
}

.org-kb-preview__title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--y-text);
}

.org-kb-preview__body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.org-kb-preview--loading {
  flex: 1;
  min-height: 120px;
}

.org-kb-preview__pdf {
  flex: 1;
  width: 100%;
  min-height: 0;
  border: 0;
  background: rgba(15, 23, 42, 0.6);
}

.org-kb-preview__text {
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: 12px 16px;
  overflow-y: auto;
  font-size: var(--y-font-size-xs);
  line-height: 1.55;
  color: var(--y-text);
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
}

.org-kb-preview__empty-msg {
  font-size: 0.75rem;
  color: var(--y-text-muted);
}

.org-detail-list__row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.org-detail-list__icon {
  flex-shrink: 0;
  color: var(--y-text-muted);
}

.org-detail-list__value {
  font-size: var(--y-font-size-sm);
  font-weight: 500;
  color: var(--y-text);
  line-height: 1.4;
  word-break: break-word;
}

.org-detail-kb {
  padding: var(--y-space-3);
  border-radius: var(--y-radius-sm);
  background: var(--y-glass-surface);
  border: 1px solid var(--y-glass-border);
  overflow-y: auto;
  min-height: 0;
}

.org-detail-kb__text {
  margin: 0;
  font-size: var(--y-font-size-xs);
  line-height: 1.55;
  color: var(--y-text-secondary);
  white-space: pre-wrap;
}

.org-detail-kb__meta-row {
  margin-bottom: 8px;
}

.org-kb-empty {
  flex: 1;
  min-height: 200px;
  padding: var(--y-space-4);
}

.org-kb-empty__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--y-text);
}

.org-kb-empty__text {
  margin: 0;
  font-size: var(--y-font-size-sm);
  color: var(--y-text-muted);
  max-width: 320px;
}

@media (max-width: 768px) {
  .org-kb-files__layout {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }

  .org-kb-files__list {
    max-height: 180px;
  }
}
</style>
